import { theme } from '@features/executive-report/domain/theme';
import { CampaignOverviewSlide2Options } from './campaign-overview-2';
import { TextProps, parseTags } from '@features/powerpoint';
import { formatters } from '@utils/formattersV2';

const DRIVER_OFFSET = 1.5;

export type DriverTextsOptions = {
  index: number;
  tagline: TextProps['text'];
  descriptionText: TextProps['text'];
  metricName: string;
  salesValue: string;
  basketSize?: string;
};

export const driverTexts = (options: DriverTextsOptions): TextProps[] => [
  {
    text: options.tagline,
    options: {
      fontSize: 12,
      color: theme.colors.white,
      h: 0.3,
      w: 1.88,
      x: 8.65,
      y: 3.12 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.descriptionText,
    options: {
      fontSize: 8,
      color: theme.colors.white,
      h: 0.5,
      w: 2.08,
      x: 8.64,
      y: 3.56 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.metricName.toUpperCase(),
    options: {
      fontSize: 10,
      color: theme.colors.white,
      bold: true,
      align: 'center',
      h: 0.27,
      w: 2.39,
      x: 10.53,
      y: 3.15 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.basketSize ? 'Avg. Price Per Transaction'.toUpperCase() : '',
    options: {
      fontSize: 7,
      color: theme.colors.inmarketGreen,
      bold: true,
      h: 0.22,
      w: 1.1,
      x: 10.7,
      y: 3.55 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.salesValue,
    options: {
      fontSize: 18,
      color: theme.colors.white,
      h: 0.4,
      w: 1.26,
      x: options.basketSize ? 10.77 : 11.24,
      y: options.basketSize
        ? 3.82 + DRIVER_OFFSET * options.index
        : 3.52 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.basketSize ? 'Avg. Total Basket Amount'.toUpperCase() : '',
    options: {
      fontSize: 7,
      color: theme.colors.inmarketGreen,
      bold: true,
      h: 0.22,
      w: 1.15,
      x: 11.8,
      y: 3.55 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.basketSize ?? '',
    options: {
      fontSize: 18,
      color: theme.colors.white,
      h: 0.4,
      w: 1.26,
      x: 11.87,
      y: 3.82 + DRIVER_OFFSET * options.index,
    },
  },
];

export const buildDriverTextsOptions = (
  report: CampaignOverviewSlide2Options['report']
): Omit<DriverTextsOptions, 'index'>[] => [
  {
    tagline: parseTags('<b>MORE</b> <lci>PURCHASES</lci>'),
    descriptionText: parseTags(
      'This is the average transaction size in dollars for your brand and the total retailer basket size when your brand was purchased.'
    ),
    metricName: 'Transaction Size',
    salesValue: formatters.currency(report.averagePricePerTransaction, {
      decimals: 2,
    }),
    basketSize: formatters.currency(report.averageTotalBasketAmount, {
      decimals: 2,
    }),
  },
  {
    tagline: parseTags('<b>MORE</b> <lci>OFTEN</lci>'),
    descriptionText: parseTags(
      'This is the count of times that the average exposed consumer purchased your brand during the campaign timeframe.'
    ),
    metricName: 'Avg. Purchase Frequency',
    salesValue: `${formatters.number(report.averagePurchaseFrequency, {
      decimals: 2,
    })}x`,
  },
];
